import { useEffect, useRef, useState } from "react";
import { quickRace, recordRaceStart, type RoomSession } from "../lib/api";
import { getActiveProfile } from "../lib/profile";
import RaceCar from "../components/brand/RaceCar";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";

/** Matchmaking loader: spins up a Quick Race (you + bots) then hands the session
 *  to the room screen. */
export default function QuickRace({
  onReady,
  onHome,
}: {
  onReady: (session: RoomSession) => void;
  onHome: () => void;
}) {
  const [error, setError] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Use the active profile (consistent with the rest of the app) rather than
    // the pre-profiles `br:name` key.
    const name = getActiveProfile()?.displayName || "You";
    quickRace(name)
      .then((session) => {
        recordRaceStart("quick");
        onReady(session);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Couldn't start a quick race."),
      );
  }, [onReady]);

  return (
    <div className="relative min-h-full">
      <SceneBackground />
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 text-center">
        <Wordmark width={300} />
        {error ? (
          <div className="panel mt-6 w-full p-7">
            <div className="mb-3 text-5xl">😕</div>
            <p className="mb-5 font-semibold text-ink/80">{error}</p>
            <button className="btn-play w-full" onClick={onHome}>
              <span aria-hidden>↩</span> BACK TO START
              <span className="sheen animate-shine" aria-hidden />
            </button>
          </div>
        ) : (
          <div className="panel mt-6 flex flex-col items-center gap-3 px-8 py-6">
            <RaceCar width={200} number={1} spinning bobbing ariaLabel="" />
            <div className="font-display text-xl text-ink">
              Finding you a race…
            </div>
            <p className="text-sm font-semibold text-ink/55">
              Lining up racers at the start 🏁
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
